/**
 * Dropbox Service with OAuth Support
 * Handles file uploads to Dropbox with automatic token refresh
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// Token storage keys
const ACCESS_TOKEN_KEY = 'dropbox_access_token';
const TOKEN_EXPIRY_KEY = 'dropbox_token_expiry';

class DropboxService {
  constructor() {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.refreshPromise = null; // Prevent concurrent refresh calls
  }

  /**
   * Initialize the service - loads token from storage
   */
  initialize() {
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    this.tokenExpiry = expiryStr ? parseInt(expiryStr, 10) : null;
  }

  /**
   * Set access token (called after OAuth flow or refresh)
   */
  setToken(accessToken, expiresIn) {
    this.accessToken = accessToken;
    // Set expiry to 5 minutes before actual expiry for safety buffer
    this.tokenExpiry = Date.now() + ((expiresIn - 300) * 1000);
    
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, this.tokenExpiry.toString());
  }

  /**
   * Clear stored tokens (on disconnect)
   */
  clearTokens() {
    this.accessToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  /**
   * Check if Dropbox is configured (has valid token or can refresh)
   */
  isConfigured() {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Check if token is expired or about to expire
   */
  isTokenExpired() {
    if (!this.tokenExpiry) return true;
    // Consider expired if less than 5 minutes remaining
    return Date.now() >= this.tokenExpiry;
  }

  /**
   * Get a valid access token, refreshing if necessary
   */
  async getValidToken() {
    this.initialize();
    
    if (this.accessToken && !this.isTokenExpired()) {
      return this.accessToken;
    }

    // Need to refresh - prevent concurrent refresh calls
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshToken();
    }

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Refresh access token using Firebase Cloud Function
   */
  async refreshToken() {
    try {
      console.log('🔄 Refreshing Dropbox access token...');
      const functions = getFunctions();
      const refreshDropboxToken = httpsCallable(functions, 'refreshDropboxToken');
      
      const result = await refreshDropboxToken();
      
      if (result.data?.success && result.data?.accessToken) {
        this.setToken(result.data.accessToken, result.data.expiresIn || 14400);
        console.log('✅ Dropbox token refreshed successfully');
        return result.data.accessToken;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('❌ Error refreshing Dropbox token:', error);
      
      // If refresh fails, clear tokens so user knows to reconnect
      if (error.code === 'unauthenticated' || error.message?.includes('expired')) {
        this.clearTokens();
      }
      
      throw error;
    }
  }

  /**
   * Start OAuth flow - returns URL to redirect user to
   */
  async startOAuthFlow(redirectUri) {
    try {
      const functions = getFunctions();
      const getDropboxAuthUrl = httpsCallable(functions, 'getDropboxAuthUrl');
      
      const result = await getDropboxAuthUrl({ redirectUri });
      
      if (result.data?.authUrl) {
        // Store state for verification after redirect
        localStorage.setItem('dropbox_oauth_state', result.data.state);
        return result.data.authUrl;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error starting OAuth flow:', error);
      throw error;
    }
  }

  /**
   * Complete OAuth flow - exchange code for tokens
   */
  async completeOAuthFlow(code, redirectUri) {
    try {
      const functions = getFunctions();
      const exchangeDropboxCode = httpsCallable(functions, 'exchangeDropboxCode');
      
      const result = await exchangeDropboxCode({ code, redirectUri });
      
      if (result.data?.success && result.data?.accessToken) {
        this.setToken(result.data.accessToken, result.data.expiresIn || 14400);
        localStorage.removeItem('dropbox_oauth_state');
        return {
          success: true,
          accountId: result.data.accountId
        };
      } else {
        throw new Error('Failed to exchange authorization code');
      }
    } catch (error) {
      console.error('Error completing OAuth flow:', error);
      throw error;
    }
  }

  /**
   * Get Dropbox connection status
   */
  async getConnectionStatus() {
    try {
      const functions = getFunctions();
      const getDropboxStatus = httpsCallable(functions, 'getDropboxStatus');
      
      const result = await getDropboxStatus();
      return result.data;
    } catch (error) {
      console.error('Error getting Dropbox status:', error);
      return { connected: false };
    }
  }

  /**
   * Disconnect Dropbox
   */
  async disconnect() {
    try {
      const functions = getFunctions();
      const disconnectDropbox = httpsCallable(functions, 'disconnectDropbox');
      
      await disconnectDropbox();
      this.clearTokens();
      
      return { success: true };
    } catch (error) {
      console.error('Error disconnecting Dropbox:', error);
      throw error;
    }
  }

  /**
   * Upload file to Dropbox
   */
  async uploadFile(file, filename, folderPath = '/Invoices') {
    const accessToken = await this.getValidToken();
    
    if (!accessToken) {
      throw new Error('Dropbox not connected. Please connect your Dropbox account.');
    }

    try {
      // Convert File/Blob to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Upload to Dropbox
      const uploadResponse = await fetch('https://content.dropboxapi.com/2/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/octet-stream',
          'Dropbox-API-Arg': JSON.stringify({
            path: `${folderPath}/${filename}`,
            mode: 'overwrite',
            autorename: true
          })
        },
        body: arrayBuffer
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.text();
        
        // If unauthorized, try refreshing token and retry once
        if (uploadResponse.status === 401) {
          console.log('🔄 Token expired during upload, refreshing...');
          const newToken = await this.refreshToken();
          
          // Retry upload with new token
          const retryResponse = await fetch('https://content.dropboxapi.com/2/files/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/octet-stream',
              'Dropbox-API-Arg': JSON.stringify({
                path: `${folderPath}/${filename}`,
                mode: 'overwrite',
                autorename: true
              })
            },
            body: arrayBuffer
          });
          
          if (!retryResponse.ok) {
            const retryError = await retryResponse.text();
            throw new Error(`Upload failed after refresh: ${retryResponse.status} - ${retryError}`);
          }
          
          const retryResult = await retryResponse.json();
          return {
            success: true,
            path: retryResult.path_display,
            id: retryResult.id,
            url: await this.getSharedLink(retryResult.path_display)
          };
        }
        
        throw new Error(`Upload failed: ${uploadResponse.status} - ${errorData}`);
      }

      const uploadResult = await uploadResponse.json();
      console.log('✅ File uploaded successfully:', uploadResult);
      
      return {
        success: true,
        path: uploadResult.path_display,
        id: uploadResult.id,
        url: await this.getSharedLink(uploadResult.path_display)
      };
    } catch (error) {
      console.error('Error uploading to Dropbox:', error);
      throw error;
    }
  }

  /**
   * Get shared link for uploaded file
   */
  async getSharedLink(path) {
    try {
      const accessToken = await this.getValidToken();
      
      const response = await fetch('https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: path,
          settings: {
            requested_visibility: 'public'
          }
        })
      });

      if (!response.ok) {
        // If link already exists, try to get existing link
        if (response.status === 409) {
          return await this.getExistingSharedLink(path);
        }
        throw new Error(`Failed to create shared link: ${response.status}`);
      }

      const result = await response.json();
      return result.url;
    } catch (error) {
      console.error('Error creating shared link:', error);
      return null;
    }
  }

  /**
   * Get existing shared link if one already exists
   */
  async getExistingSharedLink(path) {
    try {
      const accessToken = await this.getValidToken();
      
      const response = await fetch('https://api.dropboxapi.com/2/sharing/list_shared_links', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: path
        })
      });

      if (response.ok) {
        const result = await response.json();
        return result.links && result.links.length > 0 ? result.links[0].url : null;
      }
      return null;
    } catch (error) {
      console.error('Error getting existing shared link:', error);
      return null;
    }
  }

  /**
   * List files in folder
   */
  async listFiles(folderPath = '/Invoices') {
    const accessToken = await this.getValidToken();
    
    if (!accessToken) {
      throw new Error('Dropbox not connected');
    }

    try {
      const response = await fetch('https://api.dropboxapi.com/2/files/list_folder', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: folderPath
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to list files: ${response.status}`);
      }

      const result = await response.json();
      return result.entries;
    } catch (error) {
      console.error('Error listing Dropbox files:', error);
      throw error;
    }
  }

  /**
   * Test connection
   */
  async testConnection() {
    const accessToken = await this.getValidToken();
    
    if (!accessToken) {
      throw new Error('Dropbox not connected');
    }

    try {
      const response = await fetch('https://api.dropboxapi.com/2/users/get_current_account', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Connection test failed: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        name: result.name,
        email: result.email
      };
    } catch (error) {
      console.error('Error testing Dropbox connection:', error);
      throw error;
    }
  }
}

// Create singleton instance
const dropboxService = new DropboxService();
export default dropboxService;
